import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import clsx from "clsx";
import { formatCurrencyFn, getErrorModuleStatus, isEqual, refIsRequiredError, isEmpty } from "@util/utils";
import { Alert, AutocompleteField, Button, Card, CheckboxField, DateField, MenuItem, SelectField, TextField } from "@curves/ui-base";
import { EmployeeSelect } from "@icoach/components/";
import { Grid } from "@mui/material";
import { displayDateText, getCurrentEndDay, isValidDate, parseDate, getAddMonths, getAddDays, getDiffDate } from "@util/moment";
import usePeopleApi from "@apis/usePeopleApi";
import { PEOPLE_TYPE } from "@icoach/constants/";
import { ReadTextField } from "@icoach/components";
import useDocumentApi from "@apis/useDocumentApi";
import { getCheckboxValue, setCheckboxValue } from "@icoach/documents/tools";
import { PaidList, TermContent } from "@icoach/documents/components";
import { keyStatusMap, cabinetPaymentCycleMap } from "../staticData";

// 承租人資訊 (唯讀)
export const RenterSectionByRead = props => {
	const { sourceData = {}, titleClassName = "bg-light-active" } = props;
	const { memberName, memberNo, cellPhone } = sourceData;
	return (
		<React.Fragment>
			<Card.CardHeader title="承租人資料" className={clsx("px-4", titleClassName)} />
			<Card.CardContent className="p-4">
				<Grid spacing={3} container>
					<Grid xs={4} item>
						<ReadTextField label="會員姓名">{memberName}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="會員編號">{memberNo}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="手機號碼">{cellPhone}</ReadTextField>
					</Grid>
				</Grid>
			</Card.CardContent>
		</React.Fragment>
	);
};
// 主要承租資訊 (唯讀)
export const RentMainSectionByRead = props => {
	const { titleClassName = "bg-light-active", sourceData = {} } = props;
	const { cabinetRentalNo, cabinetRentalStatusText, cabinetName, firstMonthFee, monthlyFee, paymentCycle, paymentCycleText, memo, startDate, endDate, keyStatusText, rentalMemo } = sourceData;

	return (
		<React.Fragment>
			<Card.CardHeader title="承租資料" className={clsx("px-4", titleClassName)} />
			<Card.CardContent className="p-4">
				<Grid spacing={3} container>
					<Grid xs={4} item>
						<ReadTextField label="租約編號">{cabinetRentalNo}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="租約狀態">{cabinetRentalStatusText}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="繳費週期">{paymentCycleText}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="置物櫃名稱">{cabinetName}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="租金">
							{isEqual(paymentCycle, cabinetPaymentCycleMap.payOff) ? formatCurrencyFn(firstMonthFee, { symbol: "$" }) : formatCurrencyFn(monthlyFee, { symbol: "$" })}
						</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="置物櫃備註">{memo}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="租借期間">{displayDateText(startDate, endDate)}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="鑰匙">{keyStatusText}</ReadTextField>
					</Grid>
					<Grid xs={12} item>
						<ReadTextField label="備註">{rentalMemo}</ReadTextField>
					</Grid>
				</Grid>
			</Card.CardContent>
		</React.Fragment>
	);
};
// 教練與建單日期 (唯讀)
export const TopSectionByRead = props => {
	const { className, sourceData = {} } = props;
	const { cabinetRentalCreateDate, updatedDate, badgeName } = sourceData;

	return (
		<Card className={className}>
			<Card.CardContent className="p-4">
				<Grid spacing={3} container>
					<Grid xs={4} item>
						<ReadTextField label="建立日期">{displayDateText(cabinetRentalCreateDate)}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="更新擔當">{badgeName}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="更新日期">{displayDateText(updatedDate)}</ReadTextField>
					</Grid>
				</Grid>
			</Card.CardContent>
		</Card>
	);
};
// 退租 (唯讀)
export const QuitSectionByRead = props => {
	const { titleClassName = "bg-light-active", sourceData = {} } = props;
	const { endDate, cabinetQuitKeyStatusText, quitReason } = sourceData;
	return (
		<React.Fragment>
			<Card.CardHeader title="退租資訊" className={clsx("px-4", titleClassName)} />
			<Card.CardContent className="p-4">
				<Grid spacing={3} container>
					<Grid xs={4} item>
						<ReadTextField label="退租日期">{displayDateText(endDate)}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="鑰匙狀況">{cabinetQuitKeyStatusText}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="退租原因">{quitReason}</ReadTextField>
					</Grid>
				</Grid>
			</Card.CardContent>
		</React.Fragment>
	);
};
// 信用卡資訊 唯讀
export const CreditCardInfoSectionByRead = props => {
	const { titleClassName = "bg-light-active", sourceData = {} } = props;
	const { payment = {} } = sourceData;
	const { creditCardNo, cardExpiryDate, cardName, cardRelation, creditCardTypeText } = payment;

	return (
		<React.Fragment>
			<Card.CardHeader title="會籍信用卡資訊" className={clsx("px-4", titleClassName)} />
			<Card.CardContent className="p-4">
				<Grid spacing={3} container>
					<Grid xs={4} item>
						<ReadTextField label="信用卡號">{creditCardNo}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="有效期限">{cardExpiryDate}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="卡別">{creditCardTypeText}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="持卡人姓名">{cardName}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="卡片關係人">{cardRelation}</ReadTextField>
					</Grid>
				</Grid>
			</Card.CardContent>
		</React.Fragment>
	);
};

// 會籍扣款方式與卡片持卡人關係
const MembershipInfoSection = props => {
	const { sourceData = {} } = props;
	const { cardRelation, paymentTypeText } = sourceData;
	const { getPaymentCreditCardPDFApi } = useDocumentApi();
	return (
		<Grid xs={12} item>
			<Grid spacing={3} container>
				<Grid xs={12} item>
					<Alert className="alert-justify-content-start" severity="info" variant="filled" color="info">
						目前只提供信用卡方式進行扣款，我們將於「契約時間」，以您會籍的扣款方式每月繳付置物櫃承租金額。
						<br />
						如信用卡非會員本人持有，請列印「信用卡授權書」請卡片持卡人授權，謝謝。
					</Alert>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label="會籍的扣款方式">{paymentTypeText}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label="信用卡持卡關係">{cardRelation}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<Button className="mt-4" variant="outlined" onClick={getPaymentCreditCardPDFApi}>
						信用卡授權書
					</Button>
				</Grid>
			</Grid>
		</Grid>
	);
};

// 教練與建單日期
export const TopSection = React.forwardRef((props, ref) => {
	const { className, resource = {}, isCreate = false } = props;
	const { employeeOption = [] } = resource;
	const dateRef = useRef(null);
	const employeeRef = useRef(null);

	const getResult = () => {
		let result = {};
		if (dateRef.current && dateRef.current.getResult)
			result = Object.assign(result, {
				cabinetRentalCreateDate: dateRef.current.getResult(),
			});
		if (employeeRef.current && employeeRef.current.getResult) {
			result = Object.assign(result, { employeeID: employeeRef.current.getResult() });
		}

		return result;
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult,
			isError: () => refIsRequiredError(dateRef, employeeRef),
		}),
		// eslint-disable-next-line
		[]
	);

	return (
		<Card className={className}>
			<Card.CardContent className="p-4">
				<Grid spacing={3} container>
					{isCreate && (
						<Grid xs={4} item>
							<DateField ref={dateRef} label="建立日期" defaultValue={parseDate()} fullWidth />
						</Grid>
					)}
					<Grid xs={4} item>
						<EmployeeSelect ref={employeeRef} options={employeeOption} label="擔當" fullWidth required />
					</Grid>
				</Grid>
			</Card.CardContent>
		</Card>
	);
});

// 承租人資訊
export const RenterSection = React.forwardRef((props, ref) => {
	const { sourceData = {}, titleClassName = "bg-light-active", doCheckDebitPersonalApi } = props;
	const { memberID } = sourceData;
	const { people: memberOptions = [] } = usePeopleApi(PEOPLE_TYPE.MEMBER);
	const [memberData, setMemberData] = useState({});
	const memberRef = useRef(null);

	const getResult = () => {
		let result = {};
		if (memberRef.current && memberRef.current.getResult) {
			const { id } = memberRef.current.getResult();
			result = Object.assign(result, { memberID: id });
		}

		return result;
	};

	const handleChangeMember = newValue => {
		let value = newValue;
		if (isEmpty(value)) value = {};
		setMemberData(value);
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult,
			isError: () => refIsRequiredError(memberRef),
		}),
		// eslint-disable-next-line
		[]
	);

	useEffect(() => {
		if (!isEmpty(memberData)) {
			doCheckDebitPersonalApi(memberData.id);
		}
		// eslint-disable-next-line
	}, [memberData]);

	useEffect(() => {
		if (!isEmpty(memberOptions) && memberID) {
			let _memberData = memberOptions.find(item => isEqual(item.id, memberID));
			setMemberData(_memberData);
		}
	}, [memberID, memberOptions]);

	return (
		<React.Fragment>
			<Card.CardHeader title="承租人資料" className={clsx("px-4", titleClassName)} />
			<Card.CardContent className="p-4">
				<Grid spacing={3} container>
					<Grid xs={4} item>
						<AutocompleteField
							key={memberData?.id}
							ref={memberRef}
							label="會員"
							defaultValue={memberData?.id}
							options={memberOptions}
							optionKeys={["name", "nickName", "mobile", "no"]}
							optionMaskKeys={["maskName", "nickName", "maskMobile", "no"]}
							primaryKey={"id"}
							inputProps={{
								placeholder: "請輸入會員姓名/手機號碼/會員編號，擇一即可",
							}}
							onChange={handleChangeMember}
							fullWidth
							required
						/>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="會員編號">{memberData?.no}</ReadTextField>
					</Grid>
				</Grid>
			</Card.CardContent>
		</React.Fragment>
	);
});

// 主要承租資訊
export const RentMainSection = React.forwardRef((props, ref) => {
	const {
		titleClassName = "bg-light-active",
		sourceData = {},
		membershipData = {},
		cabinetData = {},
		resource = {},
		isCreate = false,
		setCabinetData,
		handlePaymentCycleChange,
		setCabinetFee,
	} = props;
	const { startDate: startDateProps, endDate: endDateProps, keyStatus = keyStatusMap.inClub, rentalMemo, paymentCycle, firstMonthFee } = sourceData;
	const { cabinetID = 0, cabinetFee = 0, memo } = cabinetData;
	const { cabinets = [], cabinetKeyStatusOption = [], cabinetPaymentCycleOption = [] } = resource;
	const [endDate, setEndDate] = useState(endDateProps);
	const [startDate, setStartDate] = useState(startDateProps);
	const cabinetRef = useRef(null);
	const keyRef = useRef(null);
	const memoRef = useRef(null);
	const startDateRef = useRef(null);
	const endDateRef = useRef(null);
	const paymentCycleRef = useRef(null);
	const rentFeeRef = useRef(null);
	const isEdit = sourceData.cabinetRentalID && !isEqual(sourceData.cabinetRentalID, 0);
	const isPaymentCyclePayOff = isEqual(paymentCycle, cabinetPaymentCycleMap.payOff);
	const isPaymentCycleMonthly = isEqual(paymentCycle, cabinetPaymentCycleMap.monthly);

	const handleChangeCabinet = val => {
		let _val = {};
		if (val) {
			_val = val;
		}
		setCabinetData(_val);
	};

	const getDefaultEndDate = startDate => {
		return getAddDays(getAddMonths(startDate, 12), -1);
	};

	const handleStartDateChange = (e, value) => {
		setStartDate(value);
	};

	const handleEndDateChange = (e, value) => {
		setEndDate(value);
	};

	const handleCalculateCycles = (endDate, startDate) => {
		const _startDate = startDate || startDateRef.current.getResult();
		const _endDate = getAddDays(endDate, 1); // 好計算一個週期
		return getDiffDate(_endDate, _startDate, "month");
	};

	const getResult = () => {
		let result = {};
		if (cabinetRef.current && cabinetRef.current.getResult) {
			const { cabinetID, cabinetFee } = cabinetRef.current.getResult();
			Object.assign(result, {
				cabinetID: cabinetID,
				monthlyFee: cabinetFee,
			});
		}
		if (keyRef.current && keyRef.current.getResult) {
			Object.assign(result, { keyStatus: keyRef.current.getResult() });
		}
		if (memoRef.current && memoRef.current.getResult) {
			Object.assign(result, { rentalMemo: memoRef.current.getResult() });
		}
		if (paymentCycleRef.current && paymentCycleRef.current.getResult) {
			Object.assign(result, { paymentCycle: paymentCycleRef.current.getResult() });
		}
		if (startDateRef.current && startDateRef.current.getResult) {
			Object.assign(result, { startDate: parseDate(startDateRef.current.getResult()) });
		}
		if (endDateRef.current && endDateRef.current.getResult) {
			let endDate = null;
			if (isEqual(result.paymentCycle, cabinetPaymentCycleMap.payOff)) {
				endDate = getCurrentEndDay(parseDate(endDateRef.current.getResult()));
			}
			Object.assign(result, { endDate });
		}
		if (rentFeeRef.current && rentFeeRef.current.getResult) {
			Object.assign(result, { firstMonthFee: rentFeeRef.current.getResult() });
		}

		return result;
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult,
			isError: () => refIsRequiredError(cabinetRef, keyRef, memoRef, startDateRef, endDateRef, paymentCycleRef),
		}),
		// eslint-disable-next-line
		[]
	);

	useEffect(
		() => {
			if (isEdit) return;

			let _cabinetFee = 0;
			let qty = 1;
			if (!isEmpty(cabinetData)) {
				_cabinetFee = cabinetData.cabinetFee;
				let _startDate = parseDate();
				setStartDate(_startDate);
				if (isEqual(paymentCycle, cabinetPaymentCycleMap.payOff)) {
					let _endDate = getDefaultEndDate(_startDate);
					setEndDate(_endDate);
				}
			}

			setCabinetFee(_cabinetFee * qty);
		},
		// eslint-disable-next-line
		[cabinetData, paymentCycle]
	);

	useEffect(
		() => {
			if (isEdit) return;

			if (isEqual(paymentCycle, cabinetPaymentCycleMap.payOff)) {
				const startDate = startDateRef.current.getResult();
				setEndDate(getDefaultEndDate(startDate));
			}
		},
		// eslint-disable-next-line
		[paymentCycle]
	);

	useEffect(
		() => {
			if (isEdit) return;

			let qty = 1;

			if (isValidDate(endDate) && isValidDate(startDate)) {
				qty = handleCalculateCycles(endDate, startDate);
			}

			if (isEqual(paymentCycle, cabinetPaymentCycleMap.payOff) && (!isValidDate(endDate) || !isValidDate(startDate))) {
				qty = 0;
			}

			setCabinetFee(cabinetFee * qty);
		},
		// eslint-disable-next-line
		[endDate, startDate]
	);

	useEffect(
		() => {
			if (isValidDate(endDateProps)) {
				setEndDate(endDateProps);
			}
		},
		// eslint-disable-next-line
		[endDateProps]
	);

	useEffect(
		() => {
			let _startDate;
			if (isValidDate(startDateProps)) {
				_startDate = startDateProps;
			} else {
				_startDate = parseDate();
			}
			setStartDate(_startDate);
		},
		// eslint-disable-next-line
		[startDateProps]
	);

	return (
		<React.Fragment>
			<Card.CardHeader title="承租資料" className={clsx("px-4", titleClassName)} />
			<Card.CardContent className="p-4">
				<Grid spacing={3} container>
					<Grid xs={4} item>
						<AutocompleteField
							key={cabinetID}
							ref={cabinetRef}
							label="選擇置物櫃"
							defaultValue={cabinetID}
							options={cabinets}
							optionKeys={["cabinetName", "cabinetNo"]}
							primaryKey={"cabinetID"}
							inputProps={{
								placeholder: "請輸入置物櫃名稱或編號",
							}}
							onChange={handleChangeCabinet}
							fullWidth
							required
						/>
					</Grid>
					{isPaymentCyclePayOff && isEdit ? (
						<Grid xs={4} item>
							<TextField key={firstMonthFee} ref={rentFeeRef} label="租金" defaultValue={firstMonthFee} fullWidth />
						</Grid>
					) : (
						<Grid xs={4} item>
							<ReadTextField label="月租定價">{formatCurrencyFn(cabinetFee)}</ReadTextField>
						</Grid>
					)}

					<Grid xs={4} item>
						<ReadTextField label="置物櫃備註">{memo}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<SelectField key={paymentCycle} ref={paymentCycleRef} label="繳費週期" defaultValue={paymentCycle} onChange={handlePaymentCycleChange} disabled={isEdit} fullWidth required>
							{Array.isArray(cabinetPaymentCycleOption) &&
								cabinetPaymentCycleOption
									.filter(item => !isEqual(item.value, 99))
									.map(({ text, value }) => {
										return (
											<MenuItem value={value} key={value}>
												{text}
											</MenuItem>
										);
									})}
						</SelectField>
					</Grid>
					<Grid xs={8} item />
					{isCreate && isPaymentCycleMonthly && <MembershipInfoSection sourceData={membershipData} />}
					{isCreate && isPaymentCyclePayOff && (
						<Grid xs={12} item>
							<Alert className="alert-justify-content-start" severity="info" variant="filled" color="info">
								iCoach預設一次付清的金額是「置物櫃月租金Ｘ12個月」，如果這不是妳想要的，妳可以隨時調整租借期間與費用，調整之前要記得確認好要承租的置物櫃與支付週期，避免iCoach2重新算一次喔！
							</Alert>
						</Grid>
					)}
					<Grid xs={4} item>
						<DateField key={startDate} ref={startDateRef} label="租借起始日" defaultValue={startDate} onChange={handleStartDateChange} fullWidth required />
					</Grid>
					{isPaymentCyclePayOff && (
						<Grid xs={4} item>
							<DateField key={endDate} ref={endDateRef} label="租借結束日" defaultValue={endDate} onChange={handleEndDateChange} fullWidth required />
						</Grid>
					)}
					<Grid xs={4} item>
						<SelectField key={keyStatus} ref={keyRef} label="鑰匙" defaultValue={keyStatus} fullWidth required>
							{Array.isArray(cabinetKeyStatusOption) &&
								cabinetKeyStatusOption.map(({ text, value }) => (
									<MenuItem value={value} key={value}>
										{text}
									</MenuItem>
								))}
						</SelectField>
					</Grid>
					<Grid xs={12} item>
						<TextField ref={memoRef} label="備註" defaultValue={rentalMemo} minRows={3} multiline fullWidth />
					</Grid>
					{!!isPaymentCyclePayOff && !!isEdit && (
						<Grid xs={12} item>
							<Alert color="info" severity="info" variant="filled">
								如果變動租櫃費用要記得到收入總覽處理訂單金額與發票喔。
							</Alert>
						</Grid>
					)}
				</Grid>
			</Card.CardContent>
		</React.Fragment>
	);
});
// 費用資訊
export const FeeSection = React.forwardRef((props, ref) => {
	const { cabinetData = {}, titleClassName = "bg-light-active", cabinetFee, paymentCycle, errorModule } = props;
	const { isImmediatelyIssuing: isImmediatelyIssuingProps = true, cashAmount: cashAmountProps, creditCardAmount: creditCardAmountProps } = cabinetData;
	const [total, setTotal] = useState(0);
	const [isImmediatelyIssuing, setIsImmediatelyIssuing] = useState(isImmediatelyIssuingProps);
	// 如果初始未勾選，金額應為 0
	const initialCashAmount = isImmediatelyIssuingProps ? (cashAmountProps || 0) : 0;
	const initialCardAmount = isImmediatelyIssuingProps ? (creditCardAmountProps || 0) : 0;
	const [cashPaymentAmount, setCashPaymentAmount] = useState(initialCashAmount);
	const [cardPaymentAmount, setCardPaymentAmount] = useState(initialCardAmount);
	const isPaymentReadOnly = !isImmediatelyIssuing;
	const paymentRef = useRef(null);
	const rentCabinetFeeRef = useRef(null);
	const processingFeeRef = useRef(null);
	const immediatelyIssuingRef = useRef(null);
	const hasSyncedFromPropsRef = useRef(false);

	const getResult = () => {
		let result = {};
		// 使用 state 的值確保正確性
		Object.assign(result, {
			cashAmount: Number(cashPaymentAmount) || 0,
			creditCardAmount: Number(cardPaymentAmount) || 0,
		});
		if (rentCabinetFeeRef.current && rentCabinetFeeRef.current.getResult) {
			Object.assign(result, {
				firstMonthFee: rentCabinetFeeRef.current.getResult(),
			});
		}
		if (processingFeeRef.current && processingFeeRef.current.getResult) {
			Object.assign(result, {
				processingFee: processingFeeRef.current.getResult(),
			});
		}
		if (immediatelyIssuingRef.current && immediatelyIssuingRef.current.getResult) {
			Object.assign(result, {
				isImmediatelyIssuing: getCheckboxValue(immediatelyIssuingRef.current.getResult(), {
					isMultiple: false,
				}),
			});
		}
		return result;
	};

	const calculateFeesTotal = () => {
		let total = 0,
			firstMonthFee = 0,
			processingFee = 0;

		if (rentCabinetFeeRef.current && rentCabinetFeeRef.current.getResult) firstMonthFee = rentCabinetFeeRef.current.getResult();
		if (processingFeeRef.current && processingFeeRef.current.getResult) processingFee = processingFeeRef.current.getResult();

		total += Number(firstMonthFee);
		total += Number(processingFee);

		setTotal(total);
	};

	const handleImmediatelyIssuingChange = (e) => {
		const checked = e.target.checked;
		setIsImmediatelyIssuing(checked);
		if (!checked) {
			setCashPaymentAmount(0);
			setCardPaymentAmount(0);
		}
	};

	const handleCashPaymentChange = (e) => {
		setCashPaymentAmount(e.target.value);
	};

	const handleCardPaymentChange = (e) => {
		setCardPaymentAmount(e.target.value);
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult,
			isError: () => refIsRequiredError(paymentRef, rentCabinetFeeRef, processingFeeRef, immediatelyIssuingRef),
		}),
		// eslint-disable-next-line
		[cashPaymentAmount, cardPaymentAmount]
	);

	useEffect(
		() => {
			calculateFeesTotal();
		},
		// eslint-disable-next-line
		[cabinetFee]
	);

	useEffect(
		() => {
			// 只從 props 同步一次，避免父層 cabinetData 變動時覆寫使用者已輸入的金額
			if (hasSyncedFromPropsRef.current) return;
			hasSyncedFromPropsRef.current = true;

			// 初始化金額和 checkbox 狀態
			setIsImmediatelyIssuing(isImmediatelyIssuingProps);
			// 如果未勾選，金額應為 0
			if (!isImmediatelyIssuingProps) {
				setCashPaymentAmount(0);
				setCardPaymentAmount(0);
			} else {
				if (cashAmountProps !== undefined) {
					setCashPaymentAmount(cashAmountProps || 0);
				}
				if (creditCardAmountProps !== undefined) {
					setCardPaymentAmount(creditCardAmountProps || 0);
				}
			}
		},
		// eslint-disable-next-line
		[cashAmountProps, creditCardAmountProps, isImmediatelyIssuingProps]
	);

	return (
		<React.Fragment>
			<Card.CardHeader title="費用明細" className={clsx("px-4", titleClassName)} />
			<Card.CardContent className="p-4">
				<Grid spacing={3} container>
					<Grid xs={6} item>
						<PaidList
							ref={paymentRef}
							total={total}
							cashPaymentAmountProps={{
								key: cashPaymentAmount,
								value: cashPaymentAmount,
								readOnly: isPaymentReadOnly,
								onChangeNative: handleCashPaymentChange,
								...getErrorModuleStatus(errorModule, "cashAmount")
							}}
							cardPaymentAmountProps={{
								key: cardPaymentAmount,
								value: cardPaymentAmount,
								readOnly: isPaymentReadOnly,
								onChangeNative: handleCardPaymentChange,
								...getErrorModuleStatus(errorModule, "creditCardAmount")
							}}
						>
							<PaidList.PaidListItem label={isEqual(paymentCycle, cabinetPaymentCycleMap.payOff) ? "既存租櫃費" : "首月租櫃費"}>
								<TextField key={cabinetFee} ref={rentCabinetFeeRef} defaultValue={cabinetFee} onChangeNative={calculateFeesTotal} maskType="MONEY" required />
							</PaidList.PaidListItem>
							<PaidList.PaidListItem label="工本費">
								<TextField ref={processingFeeRef} defaultValue={0} onChangeNative={calculateFeesTotal} maskType="MONEY" required />
							</PaidList.PaidListItem>
						</PaidList>
					</Grid>
					<Grid xs={12} item>
						<CheckboxField
							ref={immediatelyIssuingRef}
							defaultChecked={setCheckboxValue(isImmediatelyIssuing)}
							className="mb-1"
							onChange={handleImmediatelyIssuingChange}
						>
							<CheckboxField.Checkbox label="是否收款並直接開立發票" value={true} />
						</CheckboxField>
						<Alert className="alert-justify-content-start" severity="info" variant="filled" color="info">
							若未直接開立，需到 新增收入 確認結帳方可確認收款及開立
						</Alert>
					</Grid>
				</Grid>
			</Card.CardContent>
		</React.Fragment>
	);
});
// 訂購條款
export const TermSection = React.forwardRef((props, ref) => {
	const { className, sourceData } = props;
	const confirmRef = useRef(null);
	const signImgRef = useRef(null);

	const getResult = () => {
		let result = {};
		if (signImgRef.current && signImgRef.current.getResult) result = Object.assign(result, { signImg: signImgRef.current.getResult() });

		return result;
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult,
			isError: () => refIsRequiredError(confirmRef, signImgRef),
		}),
		// eslint-disable-next-line
		[]
	);

	return <TermContent className={className} title="置物櫃租用注意事項" sourceData={sourceData}></TermContent>;
});
